import React, { useState } from 'react'
import {
    FormGroup,
    useTheme,
    Divider,
    Button,
    Card,
    Checkbox,
    FormControlLabel,
    Switch,
    Tab,
    Tabs,
} from '@material-ui/core'
import FlagIcon from '@material-ui/icons/Flag'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'

import { Container, Finder, MultiSelector, SideDrawer } from '../../components'
import { CustomStatus, Folder, InventoryConfig, UnitConfig } from '../../models'
import {
    InspectionCompletionFilter,
    InspectionHandledFilter,
} from '../../store'
import { FinderSelection } from '../../hooks'
import { useInspectionFilter } from '../../hooks/useFilter'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    open: boolean
    unitConfigList: UnitConfig[]
    inventoryConfigList: InventoryConfig[]
    customStatusList: CustomStatus[]
    tree: Folder
    finderSelection: FinderSelection
    makeFilteredRequest: () => void
    handleClose: () => void
}

const FILTER_TAB = 0
const ASSET_TAB = 1

export const FilterForm = (props: Props) => {
    const {
        open,
        handleClose,
        unitConfigList,
        inventoryConfigList,
        customStatusList,
        tree,
        finderSelection,
        makeFilteredRequest,
    } = props

    const {
        filter,
        resetFilters,
        setCustomStatusFilter,
        toggleUnreadFilter,
        toggleHasMessages,
        toggleHasFlagged,
        toggleHasCost,
        handleChangeMultiSelect,
        setInspectionStatusFilter,
        setHandled,
        toggleDamageFilter,
    } = useInspectionFilter()

    const theme = useTheme()
    const [tab, setTab] = useState(FILTER_TAB)

    const renderStatusCheckboxes = (csList: CustomStatus[]) => {
        return csList.map((cs) => {
            return (
                <FormControlLabel
                    key={`CS_CHECK_${cs.id}`}
                    control={
                        <Checkbox
                            color="primary"
                            checked={filter.customStatusFilter[cs.id] === true}
                            onChange={() => setCustomStatusFilter(cs)}
                        />
                    }
                    label={
                        <Container style={{ alignItems: 'center' }}>
                            <span>{cs.name}</span>
                            {cs.causes_flag && <FlagIcon />}
                        </Container>
                    }
                />
            )
        })
    }

    return (
        <SideDrawer
            open={open}
            handleClose={handleClose}
            title="Filter"
            width={400}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    flex: 1,
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                    <Tab label="Filters" value={FILTER_TAB} />
                    <Tab label="Assets" value={ASSET_TAB} />
                </Tabs>

                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflowY: 'auto',
                        height: 'calc(100% - 118px)',
                    }}
                >
                    {tab === FILTER_TAB && (
                        <Container style={{ flexDirection: 'column' }}>
                            <Card
                                style={{
                                    padding: theme.spacing(2),
                                }}
                            >
                                <FormGroup>
                                    <MultiSelector
                                        label="Unit Types"
                                        keyIdentifier="UNIT_CONFIG"
                                        options={unitConfigList}
                                        selectedOptions={Object.keys(
                                            filter.unitConfigFilter,
                                        ).map(Number)}
                                        onChange={(newOpts) => {
                                            handleChangeMultiSelect(
                                                newOpts,
                                                'unitConfigFilter',
                                            )
                                        }}
                                    />

                                    <MultiSelector
                                        label="Inventory Types"
                                        keyIdentifier="INV_CONFIG"
                                        options={inventoryConfigList}
                                        selectedOptions={Object.keys(
                                            filter.invConfigFilter,
                                        ).map(Number)}
                                        onChange={(newOpts) => {
                                            handleChangeMultiSelect(
                                                newOpts,
                                                'invConfigFilter',
                                            )
                                        }}
                                    />
                                </FormGroup>
                            </Card>
                            <Divider />
                            <Card style={{ padding: theme.spacing(2) }}>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight: 700,
                                    }}
                                >
                                    Status
                                </span>
                                <Container>
                                    <FormGroup>
                                        {renderStatusCheckboxes(
                                            customStatusList.slice(
                                                0,
                                                customStatusList.length / 2,
                                            ),
                                        )}
                                    </FormGroup>
                                    <div style={{ flex: 1 }} />
                                    <FormGroup>
                                        {renderStatusCheckboxes(
                                            customStatusList.slice(
                                                customStatusList.length / 2,
                                            ),
                                        )}
                                    </FormGroup>
                                </Container>
                            </Card>

                            <Divider />
                            <Card
                                style={{
                                    padding: theme.spacing(2),
                                    flexShrink: 0,
                                }}
                            >
                                <FormGroup>
                                    <FormControlLabel
                                        labelPlacement="end"
                                        label="Has Damages"
                                        style={{
                                            marginRight: theme.spacing(2),
                                        }}
                                        control={
                                            <Switch
                                                checked={filter.hasDamages}
                                                color="primary"
                                                onClick={toggleDamageFilter}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        labelPlacement="end"
                                        label="Has Unread Messages"
                                        style={{
                                            marginRight: theme.spacing(2),
                                        }}
                                        control={
                                            <Switch
                                                checked={filter.unreadFilter}
                                                color="primary"
                                                onClick={toggleUnreadFilter}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        labelPlacement="end"
                                        label="Has Messages"
                                        style={{
                                            marginRight: theme.spacing(2),
                                        }}
                                        control={
                                            <Switch
                                                checked={filter.hasMessages}
                                                color="primary"
                                                onClick={toggleHasMessages}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        labelPlacement="end"
                                        label="Has Flagged Item"
                                        style={{
                                            marginRight: theme.spacing(2),
                                        }}
                                        control={
                                            <Switch
                                                checked={filter.hasFlaggedItem}
                                                color="primary"
                                                onClick={toggleHasFlagged}
                                            />
                                        }
                                    />

                                    <FormControlLabel
                                        labelPlacement="end"
                                        label="Has Cost Driver"
                                        style={{
                                            marginRight: theme.spacing(2),
                                        }}
                                        control={
                                            <Switch
                                                checked={filter.hasCostDriver}
                                                color="primary"
                                                onClick={toggleHasCost}
                                            />
                                        }
                                    />

                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle1,
                                            ),
                                            fontWeight: 700,
                                            margin: theme.spacing(1, 0),
                                        }}
                                    >
                                        Completion Filter
                                    </span>

                                    <FormGroup>
                                        <ToggleButtonGroup
                                            value={
                                                filter.inspectionCompletionFilter
                                            }
                                            onChange={(_, nf) =>
                                                setInspectionStatusFilter(nf)
                                            }
                                            aria-label="Completion Filter"
                                            exclusive
                                        >
                                            <ToggleButton
                                                value={
                                                    InspectionCompletionFilter.Complete
                                                }
                                                aria-label="Complete"
                                            >
                                                Complete
                                            </ToggleButton>
                                            <ToggleButton
                                                value={
                                                    InspectionCompletionFilter.Assigned
                                                }
                                                aria-label="Assigned"
                                            >
                                                Assigned
                                            </ToggleButton>
                                            <ToggleButton
                                                value={
                                                    InspectionCompletionFilter.All
                                                }
                                                aria-label="All"
                                            >
                                                All
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </FormGroup>
                                </FormGroup>
                            </Card>

                            <Card
                                style={{
                                    padding: theme.spacing(2),
                                    flexShrink: 0,
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight: 700,
                                        margin: theme.spacing(1, 0),
                                    }}
                                >
                                    Handled Filter
                                </span>

                                <FormGroup>
                                    <ToggleButtonGroup
                                        value={filter.handled}
                                        onChange={(_, nf) => setHandled(nf)}
                                        aria-label="Handled Filter"
                                        exclusive
                                    >
                                        <ToggleButton
                                            value={InspectionHandledFilter.All}
                                            aria-label="All"
                                        >
                                            All
                                        </ToggleButton>
                                        <ToggleButton
                                            value={
                                                InspectionHandledFilter.Handled
                                            }
                                            aria-label="Handled"
                                        >
                                            Handled
                                        </ToggleButton>
                                        <ToggleButton
                                            value={
                                                InspectionHandledFilter.NotHandled
                                            }
                                            aria-label="Not Handled"
                                        >
                                            Not Handled
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormGroup>
                            </Card>
                        </Container>
                    )}

                    {tab === ASSET_TAB && (
                        <Container
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                minHeight: 200,
                                padding: theme.spacing(1),
                            }}
                        >
                            <Finder
                                root={tree}
                                locationSelection={finderSelection.selection}
                                useCase="inspection"
                                onClickFolder={(folder) =>
                                    finderSelection.setFinderSelection(folder)
                                }
                                onClickUnit={(unit) =>
                                    finderSelection.setFinderSelection(unit)
                                }
                                selectedIcon={<></>}
                                getUnitAdornment={(unit) => {
                                    return (
                                        <Container style={{ flex: 1 }}>
                                            <div style={{ flex: 1 }} />
                                            <Checkbox
                                                checked={
                                                    finderSelection.selection
                                                        .unit[unit.id] !==
                                                    undefined
                                                }
                                                onClick={() => {
                                                    finderSelection.setFinderSelection(
                                                        {
                                                            type: 'unit',
                                                            location: unit,
                                                        },
                                                    )
                                                }}
                                            />
                                        </Container>
                                    )
                                }}
                                getFolderAdornment={(folder) => {
                                    return (
                                        <Container style={{ flex: 1 }}>
                                            <div style={{ flex: 1 }} />
                                            <Checkbox
                                                color={'primary'}
                                                checked={
                                                    finderSelection.selection
                                                        .folder[folder.id] !==
                                                    undefined
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    finderSelection.setFinderSelection(
                                                        {
                                                            type: 'folder',
                                                            location: folder,
                                                        },
                                                    )
                                                }}
                                            />
                                        </Container>
                                    )
                                }}
                            />
                        </Container>
                    )}
                </Container>

                {/* Footer */}
                <Divider />
                <Container
                    style={{
                        padding: theme.spacing(2),
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        color="primary"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={resetFilters}
                    >
                        Reset
                    </Button>

                    <Button
                        color="secondary"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={handleClose}
                    >
                        Close
                    </Button>

                    <Button
                        color="primary"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={makeFilteredRequest}
                    >
                        Apply
                    </Button>
                </Container>
            </Container>
        </SideDrawer>
    )
}
